import React from "react";
import { Mail, Facebook, Instagram, Youtube, Music2, BoxIcon } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { QuoteBlock } from "../components/QuoteBlock";
import { WavyDivider } from "../components/WavyDivider";
import { FaqSection } from "../components/blocks/FaqSection";
import { SectionDivider, SURFACE } from "../components/blocks/SectionDivider";
import { useSeo } from "../lib/useSeo";
import { faqSchema } from "../lib/schema";
import { SITE, SOCIAL_PROFILES } from "../lib/siteConfig";
/**
 * PAGE: /lets-connect
 * WORDPRESS: page template `page-lets-connect.php`
 *
 * Blocks: contact_hero → faq → quote
 * Email + social links now read from lib/siteConfig (ACF Options page), so the
 * footer, contact page and Organization schema can never fall out of sync.
 */

const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/pjtvKuAo9uV3etLstSEUeH/10-CONTACT-01.jpg';
const SOCIAL_ICONS: Record<string, BoxIcon> = {
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  spotify: Music2
};

/** ACF: `page_faqs` repeater — high-value for AI answer engines. */
const faqs = [{
  question: 'How do I book a session?',
  answer: `Email ${SITE.email} with a short note about what you are looking for. Branka replies personally and will suggest which path — Meditation Between Worlds, Sound-Infused Renewal or Sopotje — may suit you best.`
}, {
  question: 'Are sessions available online?',
  answer: 'Meditation Between Worlds and Sopotje guidance sessions are available both online and in person. Sound-Infused Renewal Biofield Tuning is usually offered in the studio, though distance sessions can be arranged on request.'
}, {
  question: 'What languages are sessions held in?',
  answer: 'Sessions are held in English and Slovenian.'
}, {
  question: 'What if I am not sure which path is right for me?',
  answer: 'That is completely normal. Get in touch and describe where you are right now — a short conversation is usually enough to find the path that fits.'
}];
export function Contact() {
  useSeo({
    title: "Let's Connect",
    description: `Get in touch with Branka Androjna at ${SITE.name}. Book a Biofield Tuning session, guided meditation or a Sopotje guidance conversation — online or in person.`,
    path: '/lets-connect',
    image: HERO_IMAGE,
    breadcrumbs: [{
      name: "Let's Connect",
      path: '/lets-connect'
    }],
    schema: [faqSchema(faqs), {
      '@type': 'ContactPage',
      name: `Contact ${SITE.name}`,
      url: `${SITE.url}/lets-connect`
    }]
  });
  return <div className="flex min-h-screen flex-col bg-cream" data-id="element-486" data-mp="1d6xbcd980of9">
      <Header data-id="element-487" data-mp="1d8i6a3zg69dn" />

      <main data-id="element-488" data-mp="18fypyz9dl055">
        {/* BLOCK: contact_hero — ACF `block_contact_hero` */}
        <section className="relative flex min-h-[560px] items-center justify-center overflow-hidden" data-id="element-489" data-mp="18dvtpxhmqarz">
          <img src={HERO_IMAGE} alt="White brick wall with pink flowers in white vases" loading="eager" fetchPriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover" data-id="element-490" data-mp="ko1h9h182o89" />
          <div aria-hidden="true" className="absolute inset-0 bg-white/40" data-id="element-491" data-mp="2rctqgj7axaid" />

          <div className="relative z-10 mt-16 px-6 pb-24 text-center" data-id="element-492" data-mp="2w6o6ncnp77cp">
            <h1 className="mb-3 font-serif text-3xl text-charcoal md:text-4xl" data-id="element-493" data-mp="2hhi7tpsrvj5c">
              {SITE.name}
            </h1>
            <p className="mb-10 font-serif text-xl italic text-[#B89B72] md:text-2xl" data-id="element-494" data-mp="2mg3dw5upgx1p">
              With Branka Androjna
            </p>

            <p className="mb-4 font-serif text-2xl italic text-charcoal" data-id="element-495" data-mp="3f47liwwu7k71">
              I would love to hear from you.
            </p>

            <a href={`mailto:${SITE.email}`} className="mb-7 inline-flex items-center gap-2 text-[#B89B72] transition-colors hover:text-[#9A7D54] focus:outline-none focus:ring-2 focus:ring-[#B89B72] focus:ring-offset-2" data-id="element-496" data-mp="313en413f04yn">
              <span className="flex h-6 w-6 items-center justify-center rounded-full border border-[#B89B72]" data-id="element-497" data-mp="1pnq0xbxzj9yw">
                <Mail size={12} strokeWidth={1.5} aria-hidden="true" data-id="element-498" data-mp="1toryzpg61fza" />
              </span>
              <span className="font-serif text-lg italic" data-id="element-499" data-mp="29zbym3070ff7">{SITE.email}</span>
            </a>

            <ul className="flex items-center justify-center gap-5 text-[#B89B72]" data-id="element-500" data-mp="255pytbf4o60t">
              {SOCIAL_PROFILES.map(profile => {
              const Icon = SOCIAL_ICONS[profile.platform];
              return <li key={profile.platform} data-id="element-501" data-mp="1vapm091ya1ro">
                    <a href={profile.url} target="_blank" rel="noopener noreferrer me" aria-label={`${profile.label} (opens in a new tab)`} className="inline-flex transition-all hover:-translate-y-0.5 hover:text-[#9A7D54] focus:outline-none focus:ring-2 focus:ring-[#B89B72] focus:ring-offset-2" data-id="element-502" data-mp="2swmvxi29kys0">
                      <Icon size={19} strokeWidth={1.5} aria-hidden="true" data-id="element-503" data-mp="2exdsliz5pz5g" />
                    </a>
                  </li>;
            })}
            </ul>
          </div>

          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20" data-id="element-504" data-mp="17hfrqi0his2u">
            <WavyDivider fill={SURFACE.cream} data-id="element-505" data-mp="13gds402noe0c" />
          </div>
        </section>

        <section className="bg-cream px-6 py-20 md:py-24" data-id="element-506" data-mp="3f6bsuwqnshie">
          <div className="mx-auto max-w-3xl" data-id="element-507" data-mp="3j6vry5uslv76">
            <FaqSection eyebrow="Before you write" heading="Common questions" faqs={faqs} data-id="element-508" data-mp="3j7p2lhwvh4ul" />
          </div>
        </section>

        <SectionDivider from={SURFACE.cream} to={SURFACE.charcoal} data-id="element-509" data-mp="3o3vwudfworwp" />

        <QuoteBlock quote="Trust the magic of new beginnings." author="— Meister Eckhart" bgClass="bg-charcoal" textClass="text-cream" accentClass="text-rose" authorClass="text-cream-100/70" data-id="element-510" data-mp="3sxgyqgkme616" />
      </main>

      <Footer data-id="element-511" data-mp="6o610nl2rtx9" />
    </div>;
}